import { Link } from 'react-router';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useFormFactor } from '@/lib/formFactor';
import { useSession } from '@/stores/session';
import { Tag, Dot, Badge } from '@/components/Tag';
import { Icon } from '@/components/Icon';
import { Alert, ErrorState } from '@/components/States';
import { dateTime, money, num, qty } from '@/lib/format';
import { Cols, DetailFrame, KV, Kpis, Loading, Panel, Timeline, type Kpi, type TimelineItem } from '../shipments/DetailParts';
import { modeOf, ruleText, setQuantity } from '../shipments/bundle';
import { fullTime } from '../shipments/format';
import { shipmentHref } from '../shipments/ShipmentCard';
import { amountRows, KitNote, targetOf, titleOf, TypeMark } from './ExceptionBits';
import { isOpen, recommended, resolution, responsibility, statusOf, stockImpact, typeOf } from './status';
import { exceptionHref, useExceptionDetail, useExceptionEvidence } from './api';
import { ExceptionActionDialog, type ExceptionAction } from './ExceptionActionDialog';
import type { ExceptionItem } from './types';
import s from './ExceptionDetail.module.css';

function Blocker({ e }: { e: ExceptionItem }) {
  if (!isOpen(e)) return null;
  const impact = stockImpact(e);
  const where = e.order.warehouse?.name || '出货仓库';
  if (!e.canOperate) {
    return <Alert tone="info" icon="lock" title="订单还没有发货出库，暂不能处理">少发、破损、退货、补发类售后，要在产生发货出库之后才能登记和处理。</Alert>;
  }
  const stock = impact.dot === 'amber'
    ? `确认补发时会从${where}扣减现有库存，并生成补发批次和出库流水。`
    : impact.dot === 'green'
      ? `确认退货入库时会增加${where}的现有库存，并生成入库流水。`
      : '只更新异常台账，不生成库存流水。';
  if (e.status === 'PENDING') {
    return (
      <Alert tone="warn" title="这条异常还没决定处理方式">
        建议：{recommended(e)}。登记异常本身不动库存；{stock}{!e.responsibility && ' 责任方也还没判定。'}
      </Alert>
    );
  }
  return (
    <Alert tone="info" title={`处理中${e.resolutionAction ? `：${resolution[e.resolutionAction] || e.resolutionAction}` : ''}`}>
      {e.resolutionNote || `下一步：${recommended(e)}。`}{e.reshipmentShipment ? ` 补发批次 ${e.reshipmentShipment.shipmentNo} 已出库，确认签收后可结案。` : ''}
    </Alert>
  );
}

export function ExceptionDetail({ id, orderId, seed, embedded, linkFor, onChanged, requestedAction }: {
  id: string;
  orderId?: string;
  seed?: ExceptionItem;
  embedded?: boolean;
  requestedAction?: 'close' | 'resolve';
  /** iPad 分栏里点「该订单其他异常」时留在分栏（改 ?id=），其余情况进详情页 */
  linkFor?: (e: ExceptionItem) => string;
  onChanged?: () => void;
}) {
  const ff = useFormFactor();
  const can = useSession((st) => st.can);
  const { item: e, siblings, order, loading, error, reload } = useExceptionDetail(id, orderId, seed);
  const evidence = useExceptionEvidence(e?.order.id || orderId, id);
  const [action, setAction] = useState<ExceptionAction | null>(null);
  const requestedActionHandled = useRef(false);

  useEffect(() => {
    if (!e || !requestedAction || requestedActionHandled.current) return;
    requestedActionHandled.current = true;
    if (!isOpen(e)) return;
    if (requestedAction === 'close' && can('exceptions.close')) setAction({ kind: 'close', item: e });
    if (requestedAction === 'resolve' && e.canOperate && can('exceptions.resolve')) setAction({ kind: 'resolve', item: e });
  }, [can, e, requestedAction]);

  // 出错 / 加载中也保留页头：手机深层页没有 Tab 栏，返回键是唯一出口
  if (!e) {
    return (
      <DetailFrame embedded={embedded} fallback="/exceptions" title="异常详情">
        {error ? <ErrorState title="异常加载失败" message={error.message} detail={`GET /api/order-exceptions?orderId=${orderId || ''}`} onRetry={reload} /> : <Loading />}
      </DetailFrame>
    );
  }

  const st = statusOf(e.status);
  const t = targetOf(e);
  const showAmount = can('orders.amount.read');
  const amounts = showAmount ? amountRows(e) : [];
  const wide = !embedded && ff !== 'phone';
  const oLine = order?.lines.find((l) => l.id === e.orderLineId);
  const batch = e.shipmentId ? order?.shipments?.find((x) => x.id === e.shipmentId) : undefined;
  const batchLine = batch?.lines.find((l) => l.orderLine?.id === e.orderLineId);
  const reship = e.reshipmentShipmentId ? order?.shipments?.find((x) => x.id === e.reshipmentShipmentId) : undefined;
  const impact = stockImpact(e);
  const canShip = can('shipments.read');
  const batchLink = (no: string) => (canShip ? <Link to={shipmentHref(no)} className={`${s.link} tnum`}>{no}</Link> : <span className="tnum">{no}</span>);
  const unitPrice = showAmount && oLine?.unitPrice !== undefined ? num(oLine.unitPrice) : null;
  const impactAmount = unitPrice === null ? null : unitPrice * num(e.quantity);

  const kpis: Kpi[] = [{ label: '异常数量', value: qty(e.quantity), unit: '个', tone: 'bad' }];
  if (impactAmount !== null) kpis.push({ label: '影响金额', value: money(impactAmount), tone: 'warn' });
  if (batchLine) {
    kpis.push({ label: ff === 'phone' ? '该批次已发' : '该批次发货', value: qty(batchLine.quantity), unit: '个' });
    kpis.push({ label: ff === 'phone' ? '已计入损耗' : '该批次破损/补耗', value: qty(batchLine.lossQuantity), unit: '个', tone: num(batchLine.lossQuantity) ? 'warn' : undefined });
  } else if (e.orderLine) {
    kpis.push({ label: '订单行已发 / 订购', value: `${qty(e.orderLine.shippedQuantity)} / ${qty(e.orderLine.quantity)}`, unit: '个' });
  }
  if (e.type === 'RETURNED' || num(e.returnInboundQuantity) > 0) {
    kpis.push({ label: '已退货入库', value: qty(e.returnInboundQuantity), unit: '个', tone: 'hl' });
  }

  /* 时间线：由字段拼出来（老系统没有异常操作日志接口），新的在上 */
  const tl: (TimelineItem & { at: string })[] = [];
  if (batch) {
    tl.push({
      key: 'ship', at: batch.shippedAt, time: dateTime(batch.shippedAt), tone: 'ok',
      title: `${batch.createdBy?.name || '—'} 发货出库`,
      body: batchLine ? `批次 ${batch.shipmentNo}：${t.name}发货 ${qty(batchLine.quantity)} 个，破损/补耗 ${qty(batchLine.lossQuantity)} 个` : `批次 ${batch.shipmentNo}`,
    });
  }
  tl.push({
    key: 'create', at: e.createdAt, time: dateTime(e.createdAt),
    title: `${e.createdBy?.name || '—'} 登记${typeOf(e.type).label}`,
    body: <>数量 {qty(e.quantity)} 个 · {e.shipment ? `关联批次 ${e.shipment.shipmentNo}` : '订单级'}{e.note && <><br />「{e.note}」</>}</>,
  });
  if (e.reshipmentShipment) {
    const at = reship?.shippedAt || e.updatedAt;
    tl.push({
      key: 'reship', at, time: dateTime(at), tone: 'ok',
      title: `${reship?.createdBy?.name ? `${reship.createdBy.name} ` : ''}生成补发批次`,
      body: <>{batchLink(e.reshipmentShipment.shipmentNo)} · {[e.reshipmentShipment.logisticsCompany, e.reshipmentShipment.trackingNo].filter(Boolean).join(' ')}</>,
    });
  }
  if (e.returnInboundAt) {
    tl.push({ key: 'in', at: e.returnInboundAt, time: dateTime(e.returnInboundAt), tone: 'ok', title: `${e.returnInboundBy?.name || '—'} 退货入库 ${qty(e.returnInboundQuantity)} 个`, body: '已写入库流水，库存增加' });
  }
  if (e.resolvedAt) {
    tl.push({
      key: 'resolve', at: e.resolvedAt, time: dateTime(e.resolvedAt), tone: e.status === 'CLOSED' || e.status === 'RESOLVED' ? 'ok' : undefined,
      title: `${e.resolvedBy?.name || '—'} ${e.resolutionAction ? `处理为「${resolution[e.resolutionAction] || e.resolutionAction}」` : `标记为${st.label}`}`,
      body: e.resolutionNote || undefined,
    });
  }
  tl.sort((a, b) => b.at.localeCompare(a.at));
  if (isOpen(e)) tl.unshift({ key: 'now', at: '', tone: 'hl', title: e.status === 'PENDING' ? '等待决定处理方式' : '处理中', body: `下一步：${recommended(e)}` });

  const evidencePanel = (
    <Panel title="凭证">
      {evidence.loading ? <p className={s.evidenceNote}>正在读取凭证照片…</p> : evidence.error ? <p className={s.evidenceNote} role="alert">凭证加载失败：{evidence.error.message}</p> : evidence.items.length ? <div className={s.evidenceGrid}>{evidence.items.map((photo) => /^\/uploads\/[^/?#]+$/.test(photo.fileUrl) && <a key={photo.evidenceId} href={photo.fileUrl} target="_blank" rel="noreferrer"><img src={photo.fileUrl} alt={photo.label} /><span>{photo.label} · {dateTime(photo.capturedAt)}</span></a>)}</div> : <p className={s.evidenceNote}>{order?.attachments?.length ? `这张订单另有 ${order.attachments.length} 个文件，可在订单详情的「文件」页核对。` : '这条异常尚无凭证照片。'}</p>}
      <Link to={`/orders/${e.order.id}`} className={s.link}>查看订单文件</Link>
    </Panel>
  );

  const objectPanel = (
    <Panel title="异常对象">
      <KV
        rows={[
          ['产品', <>{t.name}{t.code && <small className="tnum">{t.code}{t.bundle ? ' · 套装组件' : ''}</small>}</>],
          ['所属套装', t.bundle ? (
            <span className={s.kitrow}>
              <KitNote name={t.bundle} code={t.bundleCode} />
              {oLine && <span className={`${s.rule} ${modeOf(oLine) === 'PER_GROUP' ? s.group : ''}`}>{ruleText(oLine)}</span>}
              {oLine && <small>订购 {qty(setQuantity(oLine))} 套</small>}
            </span>
          ) : undefined],
          ['订单行', e.orderLine ? <span className="tnum">订购 {qty(e.orderLine.quantity)} 个 · 已发 {qty(e.orderLine.shippedQuantity)} 个</span> : '未关联订单行'],
          ['该批次', batchLine ? <span className="tnum">发货 {qty(batchLine.quantity)} · 破损/补耗 {qty(batchLine.lossQuantity)} · 实际出库 {qty(num(batchLine.quantity) + num(batchLine.lossQuantity))} 个</span> : undefined],
        ]}
      />
      {t.bundle && <p className={s.hint}>套装异常按组件登记，数量单位是「个」；补发也按组件生成。</p>}
    </Panel>
  );

  const main = (
    <>
      <div className={s.prioritySequence}>
        {isOpen(e) && <div className={s.priorityBlocker}><Blocker e={e} /></div>}
        <div className={s.priorityKpis}><Kpis items={kpis.slice(0, 4)} /></div>
        {ff !== 'desktop' && <div className={s.priorityEvidence}>{evidencePanel}</div>}
      </div>
      <Panel title="异常明细">
        <div className={s.detailResponsive}>
          <div className={s.detailTableWrap}><table className={s.detailTable}>
            <thead><tr><th>产品</th><th>产品编号</th><th>该批次已发</th><th>异常数量</th>{unitPrice !== null && <><th>单价</th><th>影响金额</th></>}</tr></thead>
            <tbody><tr>
              <td><b>{t.name}</b>{t.bundle && <small>套装「{t.bundle}」组件</small>}</td>
              <td className="tnum">{t.code || '—'}</td>
              <td className="tnum">{batchLine ? qty(batchLine.quantity) : e.orderLine ? qty(e.orderLine.shippedQuantity) : '—'}</td>
              <td className={`${s.bad} tnum`}>{qty(e.quantity)}</td>
              {unitPrice !== null && <><td className="tnum">{money(unitPrice)}</td><td className={`${s.bad} tnum`}>{money(impactAmount)}</td></>}
            </tr></tbody>
          </table></div>
          <div className={s.detailMobile}><b>{t.name}</b><small>{t.code || '未关联产品编号'}{t.bundle ? ` · ${t.bundle} 的组件` : ''}</small><div><span>该批次已发 <strong>{batchLine ? qty(batchLine.quantity) : e.orderLine ? qty(e.orderLine.shippedQuantity) : '—'}</strong></span><span>异常 <strong className={s.bad}>{qty(e.quantity)}</strong></span></div>{unitPrice !== null && <p>单价 {money(unitPrice)} · 影响金额 <strong className={s.bad}>{money(impactAmount)}</strong></p>}</div>
        </div>
      </Panel>
      {ff !== 'desktop' && objectPanel}
      <Panel title="处理时间线">
        <Timeline items={tl} />
        {loading && !order && <p className={s.hint}>正在补充发货信息…</p>}
      </Panel>
      {ff === 'desktop' && <div className={s.priorityEvidence}>{evidencePanel}</div>}
      {ff === 'desktop' && objectPanel}
    </>
  );

  const side = (
    <>
      <Panel title="基本信息">
        <KV
          rows={[
            ['类型', <TypeMark key="t" type={e.type} />],
            ['状态', <Tag key="s" tone={st.tone} sm>{st.label}</Tag>],
            ['订单', <Link key="o" to={`/orders/${e.order.id}`} className={`${s.link} tnum`}>{e.order.orderNo}</Link>],
            ['客户', e.order.customer.name],
            ['仓库', e.order.warehouse?.name || '—'],
            ['关联批次', e.shipment ? batchLink(e.shipment.shipmentNo) : <span className="em">订单级</span>],
            ['登记人', <>{e.createdBy?.name || '—'} <span className="tnum em">{fullTime(e.createdAt)}</span></>],
            ['责任方', e.responsibility ? responsibility[e.responsibility] || e.responsibility : <span className="em">待判定</span>],
            ['索赔状态', ({ SUBMITTED: '已提交待理赔', APPROVED: '已认可待回收', REJECTED: '已拒赔', RECOVERED: '已回收' } as Record<string, string>)[e.claimStatus || ''] || '未发起'],
            ['处理结果', e.resolutionAction ? resolution[e.resolutionAction] || e.resolutionAction : <span className="em">—</span>],
            ['库存影响', <Dot key="i" tone={impact.dot}>{impact.label}</Dot>],
          ]}
        />
      </Panel>
      {amounts.length > 0 && (
        <Panel title="处理金额">
          <KV rows={[...amounts.map(([k, v]) => [k, <span key={k} className="tnum">{v}</span>] as [string, ReactNode]), ['说明', e.reshipCostNote || undefined]]} />
        </Panel>
      )}
      <Panel title={<>该订单其他异常{siblings.filter(isOpen).length > 0 ? <Badge>{siblings.filter(isOpen).length}</Badge> : siblings.length ? <Badge tone="grey">{siblings.length}</Badge> : null}</>} flush>
        {siblings.length ? (
          <ul className={s.sibs}>
            {siblings.map((x) => (
              <li key={x.id}>
                <Link to={linkFor ? linkFor(x) : exceptionHref(x)} state={{ seed: x }} replace={!!linkFor}>
                  <span className={s.sl1}><TypeMark type={x.type} /><b>{titleOf(x)}</b></span>
                  <span className={s.sl2}><Tag tone={statusOf(x.status).tone} sm>{statusOf(x.status).label}</Tag>{x.resolutionAction ? resolution[x.resolutionAction] || x.resolutionAction : recommended(x)}</span>
                  <Icon name="chev" className={s.chev} />
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <p className={s.none}>{loading ? '加载中…' : '这张订单没有别的异常。'}</p>
        )}
      </Panel>
    </>
  );

  const sub = (
    <>
      <Link to={`/orders/${e.order.id}`} className={`${s.link} tnum`}>{e.order.orderNo}</Link> · {e.order.customer.name}
      {e.shipment && <> · 批次 {batchLink(e.shipment.shipmentNo)}</>}
      {' · '}<span className={s.nw}>{e.createdBy?.name || '—'}于 <span className="tnum">{fullTime(e.createdAt)}</span> 登记</span>
    </>
  );
  // 手机：导航栏放不下长标题，改成「异常详情」，完整标题放进正文顶部（稿 X2 的 hero）
  const phone = ff === 'phone' && !embedded;

  return (
    <>
    <DetailFrame
      embedded={embedded}
      fallback="/exceptions"
      title={phone ? '异常详情' : <span className={s.title}><TypeMark type={e.type} big /><span>{titleOf(e)}</span></span>}
      tags={<Tag tone={st.tone}>{st.label}</Tag>}
      actions={
        <>
          {embedded && <Link className={s.open} to={exceptionHref(e)} state={{ seed: e }}>全屏查看</Link>}
          {isOpen(e) && can('exceptions.close') && <button type="button" className={s.closeAction} onClick={() => setAction({ kind: 'close', item: e })}>关闭异常</button>}
          {isOpen(e) && e.canOperate && can('exceptions.resolve') && <button type="button" className={`${s.closeAction} ${s.primaryAction}`} onClick={() => setAction({ kind: 'resolve', item: e })}>处理</button>}
        </>
      }
      sub={phone ? undefined : sub}
    >
      {phone && (
        <div className={s.hero}>
          <div className={s.ht}><TypeMark type={e.type} big /><b>{titleOf(e)}</b></div>
          <p>{sub}</p>
        </div>
      )}
      <Cols wide={wide} main={main} side={side} />
    </DetailFrame>
    {action && <ExceptionActionDialog action={action} can={can} onClose={() => setAction(null)} onSuccess={() => { reload(); onChanged?.(); }} />}
    </>
  );
}
